import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  User,
  Settings,
  CheckCircle,
  XCircle,
  Building,
  BadgeCheck as BadgeIcon,
  Search,
  UserPlus,
  UserMinus,
  Loader2,
} from 'lucide-react';
import { Modal, Button, Badge } from '../custom-components';
import {
  ApplicationEntity,
  AcceptedCallingAeTitle,
  Device,
  Connection,
} from '../types';
import {
  useSearchUsersWithAcceptanceStatus,
  useAddAcceptedCallingAeTitle,
  useRemoveAcceptedCallingAeTitle,
} from '../../../../helpers/api/pacs/pacs';

interface AcceptedCallingAETitlesModalProps {
  isOpen: boolean;
  onClose: () => void;
  ae: ApplicationEntity | null;
  device: Device | null;
  connection: Connection | null;
  onUpdate: (
    deviceId: string,
    connectionId: string,
    aeId: string,
    acceptedTitles: AcceptedCallingAeTitle[]
  ) => void;
}

interface UserWithStatus {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  aeTitle: string;
  isAccepted: boolean;
  acceptedCallingAeTitleId?: number;
}

export const AcceptedCallingAETitlesModal: React.FC<
  AcceptedCallingAETitlesModalProps
> = ({ isOpen, onClose, ae, device, connection, onUpdate }) => {
  const { t } = useTranslation('Organization');
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserWithStatus[]>([]);
  const [acceptedTitles, setAcceptedTitles] = useState<
    AcceptedCallingAeTitle[]
  >([]);
  const [error, setError] = useState<string | null>(null);

  const searchUsersMutation = useSearchUsersWithAcceptanceStatus();
  const addUserMutation = useAddAcceptedCallingAeTitle();
  const removeUserMutation = useRemoveAcceptedCallingAeTitle();

  useEffect(() => {
    if (isOpen && ae) {
      const aeWithTitles = ae as ApplicationEntity & {
        acceptedCallingAeTitles: AcceptedCallingAeTitle[];
      };

      if (
        aeWithTitles.acceptedCallingAeTitles &&
        aeWithTitles.acceptedCallingAeTitles.length > 0
      ) {
        setAcceptedTitles([...aeWithTitles.acceptedCallingAeTitles]);
      } else {
        setAcceptedTitles([]);
      }
    }
  }, [ae, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
      setSearchTerm('');
      setSearchResults([]);
      setError(null);
    }
  }, [isOpen]);

  const handleSearch = async () => {
    if (!searchTerm.trim() || !device || !connection || !ae) {
      return;
    }

    setError(null);

    try {
      const response = await searchUsersMutation.mutateAsync({
        deviceId: Number(device.id),
        connectionId: Number(connection.id),
        aeId: Number(ae.id),
        query: searchTerm,
      });

      setSearchResults(response.users || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setError(t('devices.modals.access.searchFailed'));
    }
  };

  const handleAddUser = async (user: UserWithStatus) => {
    if (!device || !connection || !ae) {
      return;
    }

    setError(null);

    try {
      const response = await addUserMutation.mutateAsync({
        deviceId: Number(device.id),
        connectionId: Number(connection.id),
        aeId: Number(ae.id),
        userId: user.id,
      });

      const newAcceptedTitle: AcceptedCallingAeTitle = {
        id: response.id,
        callingAeTitle: user.aeTitle,
        doctorId: user.id,
        doctorFirstName: user.firstName,
        doctorLastName: user.lastName,
        doctorRole: user.role,
        organization: 'DeepEcho',
        description: `${user.role} - ${user.email}`,
        enabled: true,
        applicationEntityId: Number(ae.id),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: null,
        updatedBy: null,
        fullDoctorName: `${user.firstName} ${user.lastName}`,
        formattedInfo: `${user.firstName} ${user.lastName} - ${user.role}`,
        doctorDisplayName: `Dr. ${user.lastName}`,
      };

      setAcceptedTitles([...acceptedTitles, newAcceptedTitle]);

      setSearchResults(
        searchResults.map((u) =>
          u.id === user.id
            ? {
                ...u,
                isAccepted: true,
                acceptedCallingAeTitleId: response.id,
              }
            : u
        )
      );

      onUpdate(device.id, connection.id, ae.id, [
        ...acceptedTitles,
        newAcceptedTitle,
      ]);
    } catch (err: unknown) {
      console.error('Error adding user:', err);
      const error = err as any;
      if (error.response?.status === 409) {
        setError(t('devices.modals.access.alreadyAccepted'));
      } else {
        setError(t('devices.modals.access.addFailed'));
      }
    }
  };

  const handleRemoveUser = async (user: UserWithStatus) => {
    if (!device || !connection || !ae || !user.acceptedCallingAeTitleId) {
      return;
    }

    setError(null);

    try {
      await removeUserMutation.mutateAsync({
        deviceId: Number(device.id),
        connectionId: Number(connection.id),
        aeId: Number(ae.id),
        acceptedTitleId: user.acceptedCallingAeTitleId,
      });

      const updatedTitles = acceptedTitles.filter(
        (title) => title.callingAeTitle !== user.aeTitle
      );
      setAcceptedTitles(updatedTitles);

      setSearchResults(
        searchResults.map((u) =>
          u.id === user.id
            ? { ...u, isAccepted: false, acceptedCallingAeTitleId: undefined }
            : u
        )
      );

      onUpdate(device.id, connection.id, ae.id, updatedTitles);
    } catch (err) {
      console.error('Error removing user:', err);
      setError(t('devices.modals.access.removeFailed'));
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSearchTerm('');
    setSearchResults([]);
    setError(null);
  };

  if (!ae) {
    return null;
  }

  const isProcessing =
    searchUsersMutation.isPending ||
    addUserMutation.isPending ||
    removeUserMutation.isPending;

  if (!ae) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEditing
          ? t('devices.modals.access.manageTitle')
          : t('devices.modals.access.viewTitle')
      }
      description={t('devices.modals.access.description', {
        mode: isEditing
          ? t('devices.modals.access.modeManage')
          : t('devices.modals.access.modeView'),
        aeTitle: ae.aeTitle,
      })}
      size="xl"
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            {t('devices.modals.access.acceptedTitles', {
              count: acceptedTitles.length,
            })}
          </h4>
          <div className="flex gap-2">
            {isEditing ? (
              <Button onClick={handleCancel} variant="outline" size="sm">
                {t('Cancel')}
              </Button>
            ) : (
              <Button
                onClick={() => setIsEditing(true)}
                variant="outline"
                size="sm"
                disabled={ae.status === 'running'}
                title={
                  ae.status === 'running'
                    ? t('devices.modals.access.cannotEditActive')
                    : t('devices.modals.access.editAccessTitle')
                }
              >
                <Settings className="h-4 w-4 mr-1" />
                {t('devices.modals.access.editAccess')}
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Warning when AE is active */}
        {!isEditing && ae.status === 'running' && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-400 px-4 py-3 rounded-lg flex items-center gap-2">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>{t('devices.modals.access.activeWarning')}</span>
          </div>
        )}

        {/* Search Section - Only visible in edit mode */}
        {isEditing && (
          <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-4">
            <h5 className="font-medium text-slate-900 dark:text-white mb-3">
              {t('devices.modals.access.searchUsers')}
            </h5>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={t('devices.modals.access.searchPlaceholder')}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-300 placeholder:text-gray-500 dark:placeholder:text-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={searchUsersMutation.isPending || !searchTerm.trim()}
                variant="primary"
                size="sm"
              >
                {searchUsersMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                {t('devices.modals.access.search')}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 max-h-64 overflow-y-auto border border-slate-200 dark:border-gray-600 rounded-lg">
                <div className="divide-y divide-slate-200 dark:divide-gray-600">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      className="p-4 hover:bg-slate-50 dark:hover:bg-gray-700 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h6 className="font-medium text-slate-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </h6>
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-gray-400">
                            <Badge variant="outline" className="text-xs">
                              {user.aeTitle || t('devices.modals.access.noAeTitle')}
                            </Badge>
                            <span>•</span>
                            <span>{user.role}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user.isAccepted ? (
                          <>
                            <Badge variant="success" className="text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {t('devices.modals.access.accepted')}
                            </Badge>
                            <Button
                              onClick={() => handleRemoveUser(user)}
                              disabled={isProcessing || !user.aeTitle}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              <UserMinus className="h-4 w-4" />
                              {t('devices.modals.access.remove')}
                            </Button>
                          </>
                        ) : (
                          <Button
                            onClick={() => handleAddUser(user)}
                            disabled={isProcessing || !user.aeTitle}
                            variant="primary"
                            size="sm"
                          >
                            <UserPlus className="h-4 w-4" />
                            {t('devices.modals.access.add')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Current Accepted Titles */}
        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg">
          <div className="p-4 border-b border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-700">
            <h5 className="font-medium text-slate-900 dark:text-white">
              {t('devices.modals.access.currentAccepted')}
            </h5>
            <p className="text-sm text-slate-600 dark:text-gray-400">
              {t('devices.modals.access.currentAcceptedHint')}
            </p>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {acceptedTitles.length === 0 ? (
              <div className="p-8 text-center text-slate-500 dark:text-gray-400">
                <Users className="h-12 w-12 mx-auto mb-4 text-slate-300 dark:text-gray-600" />
                <p>{t('devices.modals.access.noCallingAe')}</p>
                <p className="text-sm">
                  {isEditing
                    ? t('devices.modals.access.searchToAdd')
                    : t('devices.modals.access.clickEditAccess')}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-gray-600">
                {acceptedTitles.map((title) => (
                  <div
                    key={title.callingAeTitle}
                    className="p-4 hover:bg-slate-50 dark:hover:bg-gray-700"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h6 className="font-medium text-slate-900 dark:text-white">
                              {title.fullDoctorName}
                            </h6>
                            <Badge variant="outline" className="text-xs">
                              {title.callingAeTitle}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-600 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <BadgeIcon className="h-3 w-3" />
                              {title.doctorDisplayName}
                            </span>
                            <span className="flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              {title.organization}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-gray-500 mt-1">
                            {title.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {title.enabled ? (
                          <Badge
                            variant="success"
                            className="flex items-center gap-1"
                          >
                            <CheckCircle className="h-3 w-3" />
                            {t('devices.modals.access.enabled')}
                          </Badge>
                        ) : (
                          <Badge
                            variant="warning"
                            className="flex items-center gap-1"
                          >
                            <XCircle className="h-3 w-3" />
                            {t('devices.modals.access.disabled')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
